// src/components/DynamicInputs/MultiSectionSelectableOption.jsx
import React, { useState, useEffect } from 'react';
import SelectableOption from '../FormElements/SelectableOption.jsx';
import TextInput from '../FormElements/TextInput.jsx';

const MultiSectionSelectableOption = ({ schema, value: currentCompositeValue, onChange: driverOnChange }) => {
    const {
        sections = [],
        initialValue: schemaInitialValue = { selections: [], otherDetail: '' },
        componentProps = {},
        otherInputConfig: rawOtherInputConfig,
        validation: schemaValidation,
        errorMessages: schemaErrorMessages,
        layout = {},
        id: questionId,
    } = schema;

    const isMultiSelect = componentProps?.isMultiSelect || false;
    // Defensive: always use an object for otherInputConfig
    const otherInputConfig = rawOtherInputConfig || {};
    const {
        optionValue: otherOptionValue = "other",
        textInputPlaceholder,
        textInputLabel,
        textInputWrapperClassName,
        placeholder: legacyOtherPlaceholder
    } = otherInputConfig;
    const otherPlaceholder = textInputPlaceholder || legacyOtherPlaceholder || "Please specify";

    const selectionsKey = Object.keys(schemaInitialValue).find(k => Array.isArray(schemaInitialValue[k])) || 'selections';
    const otherDetailKey = Object.keys(schemaInitialValue).find(k => typeof schemaInitialValue[k] === 'string' && k !== selectionsKey) || 'otherDetail';
    const validation = schemaValidation || {};
    const errorMessages = schemaErrorMessages || {};

    const safeInitialValue = { [selectionsKey]: [], [otherDetailKey]: '', ...schemaInitialValue };
    const currentVal = currentCompositeValue || safeInitialValue;
    const currentSelections = currentVal[selectionsKey] || [];
    const currentOtherDetail = currentVal[otherDetailKey] || '';

    const [internalError, setInternalError] = useState('');

    // Check if "Other" option is present in any section
    const allSectionOptions = sections.flatMap(section => section.options || []);
    const hasOtherInSections = allSectionOptions.some(opt => opt.value === otherOptionValue);
    const hasOtherSelected = currentSelections.includes(otherOptionValue);

    useEffect(() => {
        if (!hasOtherSelected && internalError) {
            setInternalError('');
        }
        if (hasOtherSelected && currentOtherDetail.trim() === '' && validation.otherInputRequired) {
            validateAndReport(currentSelections, currentOtherDetail, true);
        } else if (hasOtherSelected && currentOtherDetail.trim() !== '') {
            setInternalError('');
        }
    }, [hasOtherSelected, currentOtherDetail, internalError, validation.otherInputRequired]);

    const validateAndReport = (newSelections, newOtherDetail) => {
        let error = '';
        const finalOtherDetail = newSelections.includes(otherOptionValue) ? newOtherDetail : '';

        if (validation.required && newSelections.length === 0) {
            error = errorMessages.required || "Selection required.";
        }
        if (!error && validation.minSelections && newSelections.length < validation.minSelections) {
            error = errorMessages.minSelections || `Please select at least ${validation.minSelections} option(s).`;
        }
        if (!error && validation.maxSelections && newSelections.length > validation.maxSelections) {
            error = errorMessages.maxSelections || `Please select no more than ${validation.maxSelections} option(s).`;
        }
        if (!error && newSelections.includes(otherOptionValue) && validation.otherInputRequired && finalOtherDetail.trim() === '') {
            error = errorMessages.otherInputRequired || "Please specify details for 'Other'.";
        }
        if (!error && newSelections.includes(otherOptionValue) && finalOtherDetail.trim() !== '' && validation.otherInputMinLength && finalOtherDetail.length < validation.otherInputMinLength) {
            error = errorMessages.otherInputMinLength || `Other details must be at least ${validation.otherInputMinLength} characters.`;
        }
        if (!error && newSelections.includes(otherOptionValue) && finalOtherDetail.trim() !== '' && validation.otherInputMaxLength && finalOtherDetail.length > validation.otherInputMaxLength) {
            error = errorMessages.otherInputMaxLength || `Other details must not exceed ${validation.otherInputMaxLength} characters.`;
        }
        setInternalError(error);
        driverOnChange({ [selectionsKey]: newSelections, [otherDetailKey]: finalOtherDetail }, error);
    };

    const handleOptionClick = (clickedOptionValue) => {
        let newSelections;
        if (isMultiSelect) {
            if (currentSelections.includes(clickedOptionValue)) {
                newSelections = currentSelections.filter(v => v !== clickedOptionValue);
            } else {
                newSelections = [...currentSelections, clickedOptionValue];
            }
        } else {
            newSelections = (currentSelections.length === 1 && currentSelections[0] === clickedOptionValue) ? [] : [clickedOptionValue];
        }
        validateAndReport(newSelections, currentOtherDetail);
    };

    const handleOtherInputChange = (event) => {
        const newOtherDetail = event.target.value;
        validateAndReport(currentSelections, newOtherDetail);
    };

    const sectionContainerClassName = layout.sectionContainerClassName || 'mb-4';
    const sectionTitleClassName = layout.sectionTitleClassName || 'h5 mb-3';
    // Use answerLayout for grid/flex if present
    const answerLayout = layout?.answerLayout || {};
    let optionsContainerStyle = {};
    if (answerLayout.type === 'flex') {
        optionsContainerStyle = {
            display: 'flex',
            flexDirection: answerLayout.direction || 'row',
            flexWrap: answerLayout.wrap || 'wrap',
            gap: answerLayout.gap
                ? /^\d+$/.test(answerLayout.gap)
                    ? `${parseFloat(answerLayout.gap) * 0.25}rem`
                    : answerLayout.gap
                : '0.5rem',
            justifyContent: answerLayout.justifyContent || 'flex-start',
            alignItems: answerLayout.alignItems || 'stretch',
        };
    } else if (answerLayout.type === 'grid') {
        const columns = answerLayout.columnsPerSection || 2;
        let gapValue = '0.5rem';
        if (answerLayout.gap) {
            if (/^\d+$/.test(answerLayout.gap)) {
                gapValue = `${parseFloat(answerLayout.gap) * 0.25}rem`;
            } else if (typeof answerLayout.gap === 'string') {
                gapValue = answerLayout.gap;
            }
        }
        optionsContainerStyle = {
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            gap: gapValue,
            alignItems: answerLayout.alignItems || 'stretch',
            justifyItems: answerLayout.justifyItems || 'stretch',
        };
    }

    return (
        <div className="multi-section-selectable-options-container mt-3">
            {sections.map((section, sectionIndex) => (
                <div key={section.id || `section-${sectionIndex}`} className={sectionContainerClassName}>
                    {section.title && <h6 className={sectionTitleClassName}>{section.title}</h6>}
                    {section.subtitle && <p className="text-muted">{section.subtitle}</p>}
                    <div style={optionsContainerStyle}>
                        {section.options.map(opt => (
                            <SelectableOption
                                key={opt.value}
                                option={opt}
                                isSelected={currentSelections.includes(opt.value)}
                                onClick={() => handleOptionClick(opt.value)}
                                className={componentProps?.optionClassName}
                            />
                        ))}
                    </div>
                </div>
            ))}
            {/* Always render the "Other" option if otherInputConfig is present and not already in any section */}
            {Object.keys(otherInputConfig).length > 0 && otherOptionValue && !hasOtherInSections && (
                <SelectableOption
                    key="other-checkbox"
                    option={{
                        value: otherOptionValue,
                        label: otherInputConfig.optionLabel || "Otro",
                        text: otherInputConfig.optionLabel || "Otro",
                    }}
                    isSelected={currentSelections.includes(otherOptionValue)}
                    onClick={() => handleOptionClick(otherOptionValue)}
                    className={componentProps?.optionClassName}
                />
            )}
            {Object.keys(otherInputConfig).length > 0 && hasOtherSelected && (
                <div className={textInputWrapperClassName ? textInputWrapperClassName : "mt-3"}>
                    {textInputLabel && (
                        <label htmlFor={`${questionId}-${otherDetailKey}`} className="form-label">
                            {textInputLabel}
                        </label>
                    )}
                    <TextInput
                        id={`${questionId}-${otherDetailKey}`}
                        name={`${questionId}-${otherDetailKey}`}
                        type="text"
                        value={currentOtherDetail}
                        onChange={handleOtherInputChange}
                        placeholder={otherPlaceholder}
                        inputClassName={componentProps?.otherInputClassName || "form-control"}
                        ariaLabel={textInputLabel || "Other specification"}
                        noWrapper={true}
                    />
                    {internalError && validation.otherInputRequired && (
                        <div className="text-danger mt-1 ms-1" style={{ fontSize: '0.875em' }}>
                            {textInputLabel
                                ? `Obligatorio`
                                : internalError
                            }
                        </div>
                    )}
                </div>
            )}
            {!hasOtherSelected && internalError && (
                <div className="text-danger mt-3 ms-1" style={{ fontSize: '0.875em' }}>
                    {internalError}
                </div>
            )}
        </div>
    );
};

export default MultiSectionSelectableOption;